import type { ComponentPropsWithRef } from 'react'
import { useId } from 'react'

export type ToggleInputSize = 'normal' | 'large'

export interface ToggleInputProps extends Omit<
    ComponentPropsWithRef<'input'>,
    'type' | 'size' | 'checked'
> {
    label: string
    size?: ToggleInputSize
    defaultChecked?: boolean
}

export function ToggleInput({
    label,
    size = 'normal',
    disabled = false,
    id,
    name,
    className = '',
    defaultChecked,
    ...inputProps
}: ToggleInputProps) {
    const generatedId = useId()
    const inputId = id ?? (name ? name : generatedId)
    const isChecked = Boolean(defaultChecked)

    return (
        <label
            htmlFor={inputId}
            className={`inline-flex items-center gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`.trim()}
        >
            <input
                {...inputProps}
                id={inputId}
                name={name}
                type="checkbox"
                role="switch"
                defaultChecked={defaultChecked}
                aria-checked={inputProps['aria-checked'] ?? isChecked}
                disabled={disabled}
                className="sr-only peer disabled:cursor-not-allowed"
            />
            {size === 'large' ? (
                <div
                    aria-hidden="true"
                    className="peer-disabled:cursor-not-allowed relative w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus-visible:ring-4 peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:inset-s-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary"
                />
            ) : (
                <div
                    aria-hidden="true"
                    className="peer-disabled:cursor-not-allowed relative w-10 h-6 bg-gray-200 peer-focus:outline-none peer-focus-visible:ring-4 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:inset-s-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"
                />
            )}
            <span
                className={`select-none ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-800'}`.trim()}
            >
                {label}
            </span>
        </label>
    )
}
